import React, { useMemo, useState } from "react";
import { Sparkles, Send, RefreshCw, Copy, MessageCircle, FileText, WandSparkles, ListTree } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const MODES = [
  { id: "draft", label: "Draft", icon: WandSparkles, help: "Turn an idea into a polished post." },
  { id: "rephrase", label: "Rephrase", icon: RefreshCw, help: "Keep your meaning, improve clarity and warmth." },
  { id: "conversation", label: "Start a conversation", icon: MessageCircle, help: "Create a thoughtful opener." },
  { id: "summarize", label: "Summarize", icon: ListTree, help: "Condense long text into the useful core." },
];

export default function AssistantPage() {
  const [mode, setMode] = useState("draft");
  const [text, setText] = useState("");
  const [context, setContext] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const active = useMemo(() => MODES.find((item) => item.id === mode), [mode]);

  const generate = async () => {
    if (!text.trim()) {
      toast.error("Give HAEVEN Companion something to work with.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/assistant/generate", {
        intent: mode,
        text: text.trim(),
        context: context.trim(),
      });
      setResult(data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "HAEVEN Companion is unavailable right now.");
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!result?.output) return;
    await navigator.clipboard.writeText(result.output);
    toast.success("Copied. You decide where to use it.");
  };

  return (
    <div className="space-y-6" data-testid="assistant-page">
      <header>
        <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-primary">
          <Sparkles size={18} /> HAEVEN Companion
        </div>
        <h1 className="font-display text-3xl md:text-5xl mt-2">Create with AI. Stay unmistakably you.</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          Companion helps you write, understand, and connect. It never posts, messages, follows, or buys anything for you.
        </p>
      </header>

      <div className="grid lg:grid-cols-[280px_1fr] gap-5">
        <aside className="space-y-2">
          {MODES.map(({ id, label, icon: Icon, help }) => (
            <button
              key={id}
              onClick={() => { setMode(id); setResult(null); }}
              className={`w-full text-left brutal-card p-4 transition-all ${mode === id ? "bg-primary text-primary-foreground" : "bg-card hover:bg-secondary"}`}
            >
              <div className="flex items-center gap-2 font-bold"><Icon size={18} /> {label}</div>
              <p className={`text-xs mt-1.5 ${mode === id ? "opacity-85" : "text-muted-foreground"}`}>{help}</p>
            </button>
          ))}
        </aside>

        <section className="brutal-card bg-card p-5 md:p-6 space-y-4">
          <div>
            <label htmlFor="assistant-text" className="font-bold text-sm">{active?.label}</label>
            <textarea
              id="assistant-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={6000}
              rows={8}
              placeholder={mode === "draft" ? "What do you want to say?" : mode === "summarize" ? "Paste the text you want summarized…" : "Write or paste your text…"}
              className="brutal-input w-full mt-2 p-3 resize-y"
              data-testid="assistant-input"
            />
            <div className="text-right text-[11px] text-muted-foreground mt-1">{text.length}/6000</div>
          </div>

          <div>
            <label htmlFor="assistant-context" className="font-bold text-sm">Optional context</label>
            <input
              id="assistant-context"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              maxLength={3000}
              placeholder="Audience, tone, community, goal, or background…"
              className="brutal-input w-full mt-2 px-3 py-2"
            />
          </div>

          <button
            onClick={generate}
            disabled={loading || !text.trim()}
            className="brutal-btn bg-primary text-primary-foreground px-5 py-3 flex items-center gap-2 disabled:opacity-50"
            data-testid="assistant-generate"
          >
            {loading ? <RefreshCw size={18} className="animate-spin" /> : <Send size={18} />}
            {loading ? "Thinking…" : "Generate"}
          </button>
        </section>
      </div>

      {result?.output && (
        <section className="brutal-card bg-secondary p-5 md:p-6" data-testid="assistant-result">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2 font-bold"><FileText size={18} /> Suggested result</div>
            <button onClick={copy} className="brutal-btn bg-card px-3 py-2 flex items-center gap-2 text-sm">
              <Copy size={16} /> Copy
            </button>
          </div>
          <div className="whitespace-pre-wrap leading-relaxed">{result.output}</div>
          {result.note && <p className="text-sm text-muted-foreground mt-4">{result.note}</p>}
          {!!result.suggestions?.length && (
            <div className="mt-4 flex flex-wrap gap-2">
              {result.suggestions.map((suggestion, index) => (
                <span key={`${suggestion}-${index}`} className="text-xs border-2 border-foreground rounded-full px-3 py-1.5 bg-card">{suggestion}</span>
              ))}
            </div>
          )}
          <p className="text-xs font-semibold mt-5">{result.disclosure || "AI-assisted draft. Review it before you use it."}</p>
        </section>
      )}
    </div>
  );
}
